/**
 * Customer and Business profile moderation scenarios (final / locked).
 * Single source of truth for Profile-type moderation cases.
 * Customer: 2 scenarios. Business: 3 scenarios.
 */

const BASE = new Date('2026-01-31T12:00:00.000Z');

const customerScenarios = [
  {
    profileType: 'Customer',
    scenarioKey: 1,
    name: 'Inappropriate / Misleading Profile Name or Avatar',
    customer: {
      customerId: 'CUS-8891',
      name: 'Admin Support Team',
      suburb: 'Werribee',
      postcode: '3030',
      accountStatus: 'Active',
      avatarNote: 'Custom image uploaded',
      priorWarnings: null,
      memberSince: '2024-06-15',
    },
    flag: {
      flagId: 'FLG-41001',
      source: 'System',
      category: 'Moderation',
      trigger: 'Misleading customer name / avatar (impersonation risk)',
      severity: 'Medium',
      status: 'Under Review',
      linkedCaseId: 'MOD-000501',
      createdAt: '2026-01-31T10:05:00.000Z',
      reportScreenshots: [
        'https://picsum.photos/seed/pc1a/800/600',
        'https://picsum.photos/seed/pc1b/800/600',
      ],
    },
    case: {
      caseId: 'MOD-000501',
      contentType: 'Profile',
      profileSubType: 'Customer',
      severity: 'Medium',
      status: 'New',
      assignedTo: 'Unassigned',
      created: '2026-01-31T10:05:00.000Z',
      updated: '2026-01-31T10:05:00.000Z',
      outcome: null,
      enforcement: null,
      escalatedTo: null,
    },
    adminNote: 'Customer profile name and avatar resemble platform staff branding, which may mislead businesses and other users. No other misuse detected. Name change and avatar removal requested without further restriction.',
    profileContent: {
      displayName: 'Admin Support Team',
      avatarNote: 'Platform-like logo image',
      profileImageUrl: 'https://picsum.photos/seed/cus8891/200/200',
    },
  },
  {
    profileType: 'Customer',
    scenarioKey: 2,
    name: 'Repeated Abusive / Misuse Behaviour',
    customer: {
      customerId: 'CUS-9042',
      name: 'Jason Patel',
      suburb: 'Point Cook',
      postcode: '3030',
      accountStatus: 'Active',
      avatarNote: 'Personal photo',
      priorWarnings: 3,
      memberSince: '2023-09-01',
    },
    flag: {
      flagId: 'FLG-41019',
      source: 'System',
      category: 'Moderation',
      trigger: 'Repeated abusive behaviour across interactions',
      severity: 'High',
      status: 'Under Review',
      linkedCaseId: 'MOD-000502',
      createdAt: '2026-01-31T11:40:00.000Z',
      reportScreenshots: [
        'https://picsum.photos/seed/pc2a/800/600',
      ],
    },
    case: {
      caseId: 'MOD-000502',
      contentType: 'Profile',
      profileSubType: 'Customer',
      severity: 'High',
      status: 'New',
      assignedTo: 'Unassigned',
      created: '2026-01-31T11:40:00.000Z',
      updated: '2026-01-31T11:40:00.000Z',
      outcome: null,
      enforcement: null,
      escalatedTo: null,
    },
    adminNote: 'Customer has received multiple prior warnings for abusive language in messages with different businesses. Behaviour has continued without improvement. Account-level action applied to prevent further misuse of the messaging feature.',
    profileContent: {
      displayName: 'Jason Patel',
      avatarNote: 'Personal photo',
      profileImageUrl: 'https://picsum.photos/seed/cus9042/200/200',
    },
  },
];

const businessScenarios = [
  {
    profileType: 'Business',
    scenarioKey: 1,
    name: 'Misrepresentation / False Credentials',
    business: {
      businessId: 'BUS-5521',
      businessName: 'Apex Plumbing',
      category: 'Plumbing / Gas',
      accountStatus: 'Active',
      description: 'Fully licensed and certified to perform all gas plumbing work across Victoria.',
      logoNote: 'Business logo',
      bannerNote: 'Banner image',
      memberSince: '2022-03-10',
    },
    flag: {
      flagId: 'FLG-51001',
      source: 'Customer',
      category: 'Moderation',
      trigger: 'Unverified or false licensing claims on business profile',
      severity: 'High',
      status: 'Under Review',
      linkedCaseId: 'MOD-000601',
      createdAt: '2026-01-31T13:20:00.000Z',
      reportScreenshots: [
        'https://picsum.photos/seed/pb1a/800/600',
        'https://picsum.photos/seed/pb1b/800/600',
      ],
    },
    case: {
      caseId: 'MOD-000601',
      contentType: 'Profile',
      profileSubType: 'Business',
      severity: 'High',
      status: 'New',
      assignedTo: 'Unassigned',
      created: '2026-01-31T13:20:00.000Z',
      updated: '2026-01-31T13:20:00.000Z',
      outcome: null,
      enforcement: null,
      escalatedTo: null,
    },
    adminNote: 'Business profile claims gas licensing that could not be verified through existing records. Due to potential consumer safety implications, misleading claims were removed and the case escalated to Compliance for formal licence verification.',
    profileContent: {
      description: 'Fully licensed and certified to perform all gas plumbing work across Victoria.',
      logoNote: 'Business logo',
      bannerNote: 'Banner image',
      profileImageUrl: 'https://picsum.photos/seed/bus5521/200/200',
    },
  },
  {
    profileType: 'Business',
    scenarioKey: 2,
    name: 'Inappropriate / Prohibited Profile Content',
    business: {
      businessId: 'BUS-6189',
      businessName: 'Urban Plumbing',
      category: 'Plumbing',
      accountStatus: 'Active',
      description: 'Standard business description (no issues)',
      logoNote: 'Contains suggestive imagery unrelated to plumbing',
      bannerNote: 'Marketing image with sexualised content',
      memberSince: '2023-11-20',
    },
    flag: {
      flagId: 'FLG-51022',
      source: 'Customer',
      category: 'Moderation',
      trigger: 'Inappropriate imagery on business profile',
      severity: 'Medium',
      status: 'Under Review',
      linkedCaseId: 'MOD-000602',
      createdAt: '2026-01-31T14:05:00.000Z',
      reportScreenshots: [
        'https://picsum.photos/seed/pb2a/800/600',
      ],
    },
    case: {
      caseId: 'MOD-000602',
      contentType: 'Profile',
      profileSubType: 'Business',
      severity: 'Medium',
      status: 'New',
      assignedTo: 'Unassigned',
      created: '2026-01-31T14:05:00.000Z',
      updated: '2026-01-31T14:05:00.000Z',
      outcome: null,
      enforcement: null,
      escalatedTo: null,
    },
    adminNote: 'Business profile contains banner and logo imagery with sexualised elements unrelated to services offered. Images removed and business instructed to upload compliant branding. No further enforcement applied.',
    profileContent: {
      description: 'No issues detected',
      logoNote: 'Image preview (read-only)',
      bannerNote: 'Image preview (read-only)',
      profileImageUrl: 'https://picsum.photos/seed/bus6189/200/200',
    },
  },
  {
    profileType: 'Business',
    scenarioKey: 3,
    name: 'Scam / Fraudulent Business Identity',
    business: {
      businessId: 'BUS-7743',
      businessName: 'Express Trade',
      category: 'Electric',
      accountStatus: 'Active',
      description: 'Trusted professionals with 10+ years experience. Fully insured and certified.',
      logoNote: 'Generic stock logo (reused elsewhere)',
      serviceAreasNote: 'Broad, non-specific coverage',
      memberSince: '2024-01-15',
    },
    flag: {
      flagId: 'FLG-51039',
      source: 'System',
      category: 'Moderation',
      trigger: 'Suspected fraudulent business profile',
      severity: 'High',
      status: 'Under Review',
      linkedCaseId: 'MOD-000603',
      createdAt: '2026-01-31T15:10:00.000Z',
      reportScreenshots: [
        'https://picsum.photos/seed/pb3a/800/600',
        'https://picsum.photos/seed/pb3b/800/600',
      ],
    },
    case: {
      caseId: 'MOD-000603',
      contentType: 'Profile',
      profileSubType: 'Business',
      severity: 'High',
      status: 'New',
      assignedTo: 'Unassigned',
      created: '2026-01-31T15:10:00.000Z',
      updated: '2026-01-31T15:10:00.000Z',
      outcome: null,
      enforcement: null,
      escalatedTo: null,
    },
    adminNote: 'Multiple indicators suggest the business profile is not a legitimate trading entity, including stock branding, unverifiable claims, and repeated customer complaints. Account suspended to prevent further harm and case escalated to Fraud & Security for full investigation.',
    profileContent: {
      description: 'Trusted professionals with 10+ years experience. Fully insured and certified.',
      logoNote: 'Stock-style logo image',
      serviceAreasNote: 'Broad, non-specific coverage',
      profileImageUrl: 'https://picsum.photos/seed/bus7743/200/200',
    },
  },
];

const allProfileScenarios = [...customerScenarios, ...businessScenarios];

/** Shared Flags-tab sample rows for Customer Account Record demos. */
export const CUSTOMER_ACCOUNT_FLAG_SAMPLES = [
  {
    id: 'flag-1',
    flagId: 'FLG-61001',
    source: 'System Flag',
    category: 'Moderation',
    reason: 'Abusive language in a Review',
    severity: 'Medium',
    status: 'Active',
    linkedCaseId: 'MOD-000701',
    linkedCaseType: 'Moderation',
    createdAt: '2026-08-01T00:20:00.000Z',
    updatedAt: '2026-08-04T10:15:00.000Z',
  },
  {
    id: 'flag-2',
    flagId: 'FLG-61002',
    source: 'User Flag',
    category: 'Messaging',
    reason: 'Abusive language in a Message Thread',
    severity: 'Low',
    status: 'New',
    messageId: 'MSG-22041',
    linkedCaseId: 'MOD-000715',
    linkedCaseType: 'Moderation',
    createdAt: '2026-07-28T14:10:00.000Z',
    updatedAt: '2026-07-28T14:10:00.000Z',
  },
  {
    id: 'flag-3',
    flagId: 'FLG-61003',
    source: 'User Flag',
    category: 'Quotes',
    reason: 'Spam quote request content',
    severity: 'Medium',
    status: 'Active',
    quoteId: 'QTE-11802',
    linkedCaseId: 'MOD-000690',
    linkedCaseType: 'Moderation',
    createdAt: '2026-07-20T09:40:00.000Z',
    updatedAt: '2026-07-22T16:05:00.000Z',
  },
  {
    id: 'flag-4',
    flagId: 'FLG-61004',
    source: 'User Flag',
    category: 'Moderation',
    reason: 'Misleading review content',
    severity: 'High',
    status: 'Active',
    linkedCaseId: 'MOD-000688',
    linkedCaseType: 'Moderation',
    createdAt: '2026-07-12T11:00:00.000Z',
    updatedAt: '2026-07-15T08:25:00.000Z',
  },
  {
    id: 'flag-5',
    flagId: 'FLG-61005',
    source: 'System Flag',
    category: 'Moderation',
    reason: 'Repeated policy breach in reviews',
    severity: 'High',
    status: 'Decided',
    linkedCaseId: 'MOD-000640',
    linkedCaseType: 'Moderation',
    createdAt: '2026-06-30T08:15:00.000Z',
    updatedAt: '2026-07-02T15:45:00.000Z',
  },
];

/** Open Flags = cases New or Active (legacy open/under review still count). */
const OPEN_FLAG_STATUSES = new Set(['new', 'active', 'open', 'under review', 'escalated']);

function countOpenCustomerFlags(flags) {
  return (flags || []).filter((f) =>
    OPEN_FLAG_STATUSES.has(String(f.status || '').toLowerCase().trim())
  ).length;
}

/** Demo list rows for Customers Screen 1 (status / verification / location coverage). */
const customersListDemoRows = [
  {
    customerId: 'CUS-10482',
    firstName: 'Olivia',
    lastName: 'Carter',
    email: 'olivia.carter@example.com',
    mobile: '0412 889 221',
    suburb: 'Melbourne',
    postcode: '3000',
    status: 'Active',
    verificationStatus: 'Verified',
    openFlagsCount: 1,
    lastActive: '2026-08-05T09:10:00.000Z',
    joined: '2026-01-12',
    avatarImg: 47,
    reviewsCount: 2,
    messagesCount: 18,
    quotesCount: 6,
    openSupportTickets: 2,
    activeAppeals: 1,
    currentActiveSessions: 2,
    failedLoginAttempts24h: 1,
    failedLoginAttempts7Days: 3,
    trustedDevices: 2,
    lastDeviceUsed: 'iPhone 16 · Safari',
    lastDeviceAdded: '2026-07-28',
    emailVerified: true,
    phoneVerified: true,
  },
  {
    customerId: 'CUS-10483',
    firstName: 'Liam',
    lastName: 'Foster',
    email: 'liam.foster@example.com',
    mobile: '0413 220 118',
    suburb: 'Geelong',
    postcode: '3220',
    status: 'Active',
    verificationStatus: 'Pending',
    openFlagsCount: 0,
    lastActive: '2026-08-05T07:40:00.000Z',
    joined: '2026-08-01',
    avatarImg: 32,
    reviewsCount: 0,
    messagesCount: 2,
    quotesCount: 1,
    openSupportTickets: 0,
    activeAppeals: 0,
    currentActiveSessions: 1,
    failedLoginAttempts24h: 0,
    failedLoginAttempts7Days: 1,
    trustedDevices: 1,
    lastDeviceUsed: 'Chrome · Windows',
    lastDeviceAdded: '2026-08-01',
    emailVerified: false,
    phoneVerified: false,
  },
  {
    customerId: 'CUS-10484',
    firstName: 'Ava',
    lastName: 'Mitchell',
    email: 'ava.mitchell@example.com',
    mobile: '0422 551 903',
    suburb: 'Frankston',
    postcode: '3199',
    status: 'Inactive',
    verificationStatus: 'Pending',
    openFlagsCount: 0,
    lastActive: '2026-06-30T16:15:00.000Z',
    joined: '2026-06-01',
    avatarImg: 12,
    reviewsCount: 0,
    messagesCount: 0,
    quotesCount: 0,
    openSupportTickets: 0,
    activeAppeals: 0,
    currentActiveSessions: 0,
    failedLoginAttempts24h: 0,
    failedLoginAttempts7Days: 0,
    trustedDevices: 1,
    lastDeviceUsed: 'Safari · macOS',
    lastDeviceAdded: '2026-06-01',
    emailVerified: false,
    phoneVerified: false,
  },
  {
    customerId: 'CUS-10485',
    firstName: 'Noah',
    lastName: 'Williams',
    email: 'noah.williams@example.com',
    mobile: '0431 774 660',
    suburb: 'Ballarat',
    postcode: '3350',
    status: 'Restricted',
    verificationStatus: 'Verified',
    openFlagsCount: 2,
    lastActive: '2026-07-20T11:30:00.000Z',
    joined: '2026-03-18',
    avatarImg: 15,
    reviewsCount: 4,
    messagesCount: 11,
    quotesCount: 3,
    openSupportTickets: 1,
    activeAppeals: 1,
    currentActiveSessions: 0,
    failedLoginAttempts24h: 2,
    failedLoginAttempts7Days: 5,
    trustedDevices: 2,
    lastDeviceUsed: 'Android · Chrome',
    lastDeviceAdded: '2026-05-10',
    emailVerified: true,
    phoneVerified: true,
  },
  {
    customerId: 'CUS-10486',
    firstName: 'Mia',
    lastName: 'Thompson',
    email: 'mia.thompson@example.com',
    mobile: '0448 102 339',
    suburb: 'Bendigo',
    postcode: '3550',
    status: 'Closed',
    verificationStatus: 'Verified',
    openFlagsCount: 0,
    lastActive: '2026-04-02T09:10:00.000Z',
    joined: '2025-12-15',
    avatarImg: 20,
    reviewsCount: 1,
    messagesCount: 4,
    quotesCount: 2,
    openSupportTickets: 0,
    activeAppeals: 0,
    currentActiveSessions: 0,
    failedLoginAttempts24h: 0,
    failedLoginAttempts7Days: 0,
    trustedDevices: 0,
    lastDeviceUsed: 'iPhone 14 · Safari',
    lastDeviceAdded: '2025-12-20',
    emailVerified: true,
    phoneVerified: true,
  },
];

/** Customers list: profile-scenario customers + Screen 1 demo coverage */
export function getCustomersForProfileScenarios() {
  const now = new Date(BASE);
  const scenarioCustomers = customerScenarios.map((s, idx) => {
    const c = s.customer;
    const f = s.flag;
    const parts = (c.name || '').split(/\s+/);
    const firstName = parts[0] || '';
    const lastName = parts.slice(1).join(' ') || '';
    const joined = new Date(f.createdAt || s.case.created);
    joined.setDate(joined.getDate() - (180 + idx * 60));
    const lastActive = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000);
    const flag = {
      id: f.flagId,
      flagId: f.flagId,
      source: f.source,
      category: f.category,
      trigger: f.trigger,
      reason: f.trigger,
      severity: s.case.severity,
      status: f.status,
      linkedCaseId: f.linkedCaseId,
      linkedCaseType: 'Moderation',
      createdAt: f.createdAt,
      updatedAt: s.case.updated || f.createdAt,
    };
    const openFlagsCount = [flag].filter((row) => {
      const st = String(row.status || '').toLowerCase().trim();
      return (
        st === 'open' ||
        st === 'under review' ||
        st === 'new' ||
        st === 'active' ||
        st === 'escalated'
      );
    }).length;

    // Scenario 2 maps to Restricted (decision outcome); scenario 1 stays Active + Verified
    const accountStatus = idx === 1 ? 'Restricted' : (c.accountStatus || 'Active');

    return {
      id: c.customerId,
      customerId: c.customerId,
      firstName,
      lastName,
      email: `customer.${(c.customerId || '').toLowerCase().replace(/^cus-?/i, '')}@example.com`,
      mobile: idx === 0 ? '0400 100 001' : '0400 200 002',
      region: 'VIC',
      location: { suburb: c.suburb || '', postcode: c.postcode || '' },
      status: accountStatus,
      accountStatus,
      verificationStatus: 'Verified',
      flagCount: 1,
      openFlagsCount,
      restrictionStatus: idx === 0 ? 'None' : 'Messaging Restricted',
      restrictionAppliedOn: idx === 0 ? null : '2026-03-28',
      restrictionExpiresOn: idx === 0 ? null : '2026-04-11',
      linkedCaseId: f.linkedCaseId || null,
      currentReviewStatus: idx === 0 ? 'None' : 'Under Review',
      underReview: true,
      flagged: true,
      directQuotes: idx === 0 ? 3 : 6,
      groupQuotes: 0,
      quotesCount: idx === 0 ? 3 : 6,
      reviewsGiven: idx === 0 ? 1 : 2,
      reviewsCount: idx === 0 ? 1 : 2,
      messageThreads: 0,
      messagesCount: 0,
      joined: joined.toISOString().split('T')[0],
      lastLogin: lastActive.toISOString().split('T')[0],
      lastActive: lastActive.toISOString(),
      emailVerified: true,
      phoneVerified: true,
      profilePicture: `https://i.pravatar.cc/150?img=${50 + idx}`,
      flags: [flag],
      scenarioKey: s.scenarioKey,
    };
  });

  const demoCustomers = customersListDemoRows.map((row, idx) => {
    const openSamples = CUSTOMER_ACCOUNT_FLAG_SAMPLES.filter((f) =>
      OPEN_FLAG_STATUSES.has(String(f.status || '').toLowerCase().trim())
    );
    const historySamples = CUSTOMER_ACCOUNT_FLAG_SAMPLES.filter((f) => {
      const s = String(f.status || '').toLowerCase().trim();
      return s === 'decided' || s === 'closed' || s === 'resolved';
    });
    // Match list Open Flags count; always keep at least one decided row for history
    const flags =
      row.openFlagsCount > 0
        ? [...openSamples.slice(0, row.openFlagsCount), ...historySamples.slice(0, 1)]
        : historySamples.length > 0
          ? historySamples
          : CUSTOMER_ACCOUNT_FLAG_SAMPLES.slice((idx % 5), (idx % 5) + 1);
    const openFlagsCount = row.openFlagsCount ?? countOpenCustomerFlags(flags);
    return {
      id: row.customerId,
      customerId: row.customerId,
      firstName: row.firstName,
      lastName: row.lastName,
      email: row.email,
      mobile: row.mobile,
      region: 'VIC',
      location: { suburb: row.suburb, postcode: row.postcode },
      status: row.status,
      accountStatus: row.status,
      verificationStatus: row.verificationStatus,
      flagCount: openFlagsCount,
      openFlagsCount: row.openFlagsCount ?? openFlagsCount,
      restrictionStatus: row.status === 'Restricted' ? 'Messaging Restricted' : 'None',
      underReview: row.status === 'Restricted',
      flagged: (row.openFlagsCount ?? openFlagsCount) > 0,
      directQuotes: row.quotesCount ?? 0,
      groupQuotes: 0,
      quotesCount: row.quotesCount ?? 0,
      reviewsGiven: row.reviewsCount ?? 0,
      reviewsCount: row.reviewsCount ?? 0,
      messageThreads: row.messagesCount ?? 0,
      messagesCount: row.messagesCount ?? 0,
      openSupportTickets: row.openSupportTickets ?? 0,
      supportTickets: row.openSupportTickets ?? 0,
      activeAppeals: row.activeAppeals ?? 0,
      currentActiveSessions: row.currentActiveSessions ?? 0,
      activeSessions: row.currentActiveSessions ?? 0,
      failedLoginAttempts24h: row.failedLoginAttempts24h ?? 0,
      failedLoginAttempts7Days: row.failedLoginAttempts7Days ?? 0,
      trustedDevices: row.trustedDevices ?? 0,
      lastDeviceUsed: row.lastDeviceUsed || '—',
      lastDeviceAdded: row.lastDeviceAdded || null,
      joined: row.joined,
      lastLogin: row.lastActive,
      lastActive: row.lastActive,
      emailVerified: row.emailVerified ?? row.verificationStatus === 'Verified',
      phoneVerified: row.phoneVerified ?? row.verificationStatus === 'Verified',
      profilePicture: `https://i.pravatar.cc/150?img=${row.avatarImg}`,
      flags,
    };
  });

  return [...demoCustomers, ...scenarioCustomers];
}

export function getCustomerScenarioByCustomerId(customerId) {
  if (!customerId) return null;
  const n = String(customerId).replace(/^CUS-?/i, '');
  return customerScenarios.find((s) => String(s.customer.customerId).replace(/^CUS-?/i, '') === n) || null;
}

/** Supplemental list-only businesses for Admin Businesses Screen 1 sample coverage. */
const businessListSamples = [
  {
    businessId: 'BUS-5522',
    businessName: 'Bright Spark Electrical',
    email: 'office@brightspark.example.com',
    mobile: '0412 000 522',
    abn: '12345678952',
    category: 'Electrical',
    suburb: 'Geelong',
    postcode: '3220',
    region: 'VIC',
    status: 'Active',
    plan: 'Basic',
    verificationStatus: 'Pending',
    openFlagsCount: 0,
    lastActive: '2026-08-05T07:45:00.000Z',
    joined: '2025-12-01',
    landline: '(03) 5222 1000',
    website: 'www.brightspark.example.com',
  },
  {
    businessId: 'BUS-5523',
    businessName: 'Coastal Carpentry',
    email: 'contact@coastalcarpentry.example.com',
    mobile: '0412 000 523',
    abn: '12345678953',
    category: 'Carpentry',
    suburb: 'Frankston',
    postcode: '3199',
    region: 'VIC',
    status: 'Inactive',
    plan: 'Pro',
    verificationStatus: 'Pending',
    openFlagsCount: 0,
    lastActive: '2026-07-28T16:15:00.000Z',
    joined: '2026-06-20',
    landline: '—',
    website: 'www.coastalcarpentry.example.com',
  },
  {
    businessId: 'BUS-5524',
    businessName: 'Premier Painting',
    email: 'admin@premierpainting.example.com',
    mobile: '0412 000 524',
    abn: '12345678954',
    category: 'Painting',
    suburb: 'Ballarat',
    postcode: '3350',
    region: 'VIC',
    status: 'Restricted',
    plan: 'Basic',
    verificationStatus: 'Verified',
    openFlagsCount: 2,
    lastActive: '2026-07-20T11:30:00.000Z',
    joined: '2024-08-14',
    landline: '(03) 5333 2200',
    website: 'www.premierpainting.example.com',
  },
  {
    businessId: 'BUS-5525',
    businessName: 'GreenLine Landscaping',
    email: 'team@greenline.example.com',
    mobile: '0412 000 525',
    abn: '12345678955',
    category: 'Landscaping',
    suburb: 'Bendigo',
    postcode: '3550',
    region: 'VIC',
    status: 'Closed',
    plan: 'Pro',
    verificationStatus: 'Verified',
    openFlagsCount: 0,
    lastActive: '2026-04-02T09:10:00.000Z',
    joined: '2023-02-10',
    landline: '(03) 5444 3300',
    website: 'www.greenline.example.com',
  },
];

/** Businesses list: profile-scenario businesses + Screen 1 sample coverage rows */
export function getBusinessesForProfileScenarios() {
  const scenarioDefaults = [
    {
      suburb: 'Melbourne',
      postcode: '3000',
      email: 'contact@apexplumbing.example.com',
      mobile: '0412 000 521',
      plan: 'Pro',
      verificationStatus: 'Verified',
      status: 'Active',
      lastActive: '2026-08-05T08:20:00.000Z',
      abn: '12345678951',
    },
    {
      suburb: 'Melbourne',
      postcode: '3000',
      email: 'contact@urbanplumbing.example.com',
      mobile: '0412 000 618',
      plan: 'Pro',
      verificationStatus: 'Verified',
      status: 'Active',
      lastActive: '2026-08-04T14:10:00.000Z',
      abn: '12345678961',
    },
    {
      suburb: 'Melbourne',
      postcode: '3000',
      email: 'contact@expresstrade.example.com',
      mobile: '0412 000 743',
      plan: 'Basic',
      verificationStatus: 'Pending',
      status: 'Restricted',
      lastActive: '2026-08-03T09:30:00.000Z',
      abn: '12345678974',
    },
  ];
  const landlines = ['(03) 9123 4567', '(03) 9666 2188', '—'];
  const websites = ['www.apexplumbing.com.au', 'www.urbanplumbing.com.au', 'www.expresstrade.com.au'];

  const fromScenarios = businessScenarios.map((s, idx) => {
    const b = s.business;
    const f = s.flag;
    const d = scenarioDefaults[idx] || scenarioDefaults[0];
    const joined = b.memberSince
      ? new Date(b.memberSince)
      : (() => {
          const j = new Date(s.case.created);
          j.setDate(j.getDate() - (90 + idx * 30));
          return j;
        })();
    const flag = {
      id: f.flagId,
      flagId: f.flagId,
      source: f.source,
      category: f.category,
      trigger: f.trigger,
      reason: f.trigger,
      severity: s.case.severity,
      status: f.status,
      linkedCaseId: f.linkedCaseId,
      linkedCaseType: 'Moderation',
      createdAt: f.createdAt,
      updatedAt: s.case.updated || f.createdAt,
    };
    const openFlagsCount = idx === 1 ? 2 : 1;
    return {
      id: b.businessId,
      businessId: b.businessId,
      businessName: b.businessName,
      mobile: d.mobile,
      email: d.email,
      businessEmail: d.email,
      abn: d.abn,
      licenseNumber: `LIC${String(100050 + idx).padStart(6, '0')}`,
      location: { suburb: d.suburb, postcode: d.postcode, state: 'VIC' },
      region: 'VIC',
      category: b.category,
      primaryTrade: b.category,
      status: d.status,
      accountStatus: d.status,
      plan: d.plan,
      verificationStatus: d.verificationStatus,
      emailVerified: true,
      phoneVerified: true,
      emailInvalid: false,
      phoneInvalid: false,
      abnVerified: true,
      licenseVerified: true,
      joined: joined.toISOString().split('T')[0],
      lastLogin: d.lastActive,
      lastActive: d.lastActive,
      subscriptionStatus: d.plan === 'Pro' ? 'Active — Pro' : 'Active — Basic',
      planType: 'Monthly',
      moderationFlags: openFlagsCount,
      fraudAlerts: 0,
      openFlagsCount,
      restrictionStatus: idx === 0 ? 'None' : idx === 1 ? 'Messaging Restricted' : 'Login Restricted',
      restrictionAppliedOn: idx === 0 ? null : '2025-10-18',
      restrictionExpiresOn: idx === 0 ? null : idx === 1 ? '2026-04-11' : '2026-01-20',
      landline: landlines[idx] || '—',
      website: websites[idx] || '—',
      linkedCaseId: s.case.caseId,
      latestFlagId: f.flagId,
      currentReviewStatus: 'Under Review',
      overviewOpenFlagsCount: openFlagsCount,
      overviewPosts: [3, 3, 1][idx],
      overviewFeaturedPosts: [2, 1, 0][idx],
      overviewQuotesSent: [2, 4, 0][idx],
      overviewReviewsReceived: [34, 18, 5][idx],
      overviewAverageRating: [4.8, 4.5, 3.1][idx],
      activeLicencesCount: [2, 2, 1][idx],
      activeBadgesCount: [2, 2, 1][idx],
      activeTicketsCount: [2, 1, 0][idx],
      roleType: 'business',
      profilePicture: `https://i.pravatar.cc/150?img=${60 + idx}`,
      primaryContactNumber: d.mobile,
      startDate: joined.toISOString().split('T')[0],
      flags: [flag],
      moderationAndFlags: [flag],
      scenarioKey: s.scenarioKey,
    };
  });

  const supplemental = businessListSamples.map((sample, idx) => {
    const flags =
      sample.openFlagsCount > 0
        ? Array.from({ length: sample.openFlagsCount }, (_, i) => ({
            id: `FLG-55${sample.businessId.slice(-2)}${i + 1}`,
            flagId: `FLG-55${sample.businessId.slice(-2)}${i + 1}`,
            source: 'System',
            category: 'Moderation',
            reason: 'Sample open flag',
            severity: 'Medium',
            status: 'Under Review',
            linkedCaseId: null,
            createdAt: sample.lastActive,
            updatedAt: sample.lastActive,
          }))
        : [];
    return {
      id: sample.businessId,
      businessId: sample.businessId,
      businessName: sample.businessName,
      mobile: sample.mobile,
      email: sample.email,
      businessEmail: sample.email,
      abn: sample.abn,
      location: { suburb: sample.suburb, postcode: sample.postcode, state: sample.region },
      region: sample.region,
      category: sample.category,
      primaryTrade: sample.category,
      status: sample.status,
      accountStatus: sample.status,
      plan: sample.plan,
      verificationStatus: sample.verificationStatus,
      emailVerified: sample.verificationStatus === 'Verified',
      phoneVerified: sample.verificationStatus === 'Verified',
      abnVerified: sample.verificationStatus === 'Verified',
      joined: sample.joined,
      lastLogin: sample.lastActive,
      lastActive: sample.lastActive,
      openFlagsCount: sample.openFlagsCount,
      overviewOpenFlagsCount: sample.openFlagsCount,
      landline: sample.landline,
      website: sample.website,
      roleType: 'business',
      profilePicture: `https://i.pravatar.cc/150?img=${70 + idx}`,
      primaryContactNumber: sample.mobile,
      flags,
      moderationAndFlags: flags,
    };
  });

  return [...fromScenarios, ...supplemental];
}

export function getBusinessScenarioByBusinessId(businessId) {
  if (!businessId) return null;
  const n = String(businessId).replace(/^BUS-?/i, '');
  return businessScenarios.find((s) => String(s.business.businessId).replace(/^BUS-?/i, '') === n) || null;
}

export function getProfileScenarioByCaseId(caseId) {
  if (!caseId) return null;
  const n = String(caseId).replace(/^(?:MODS|MOA|MOD)-?/i, '');
  return (
    allProfileScenarios.find(
      (s) => String(s.case.caseId).replace(/^(?:MODS|MOA|MOD)-?/i, '') === n
    ) || null
  );
}

export function getProfileQueueCases() {
  return allProfileScenarios.map((s) => {
    const c = s.case;
    const f = s.flag;
    // Profile → Profile name (linked entity)
    const preview = s.profileType === 'Customer'
      ? (s.customer?.name || '')
      : (s.business?.businessName || '');
    const accountAvatar = s.profileContent?.profileImageUrl || (s.profileType === 'Customer' ? s.customer?.profilePicture : s.business?.profilePicture) || (s.profileType === 'Customer' ? 'https://i.pravatar.cc/150?img=50' : 'https://i.pravatar.cc/150?img=60');
    return {
      id: c.caseId,
      caseId: c.caseId,
      contentType: 'Profile',
      preview,
      flag: f.source === 'System' ? 'System' : 'Customer',
      reason: f.trigger,
      severity: c.severity,
      status: c.status === 'In Review' ? 'Under Review' : c.status,
      escalation: 'None',
      escalationLevel: 'None',
      assignedTo: c.assignedTo,
      created: c.created,
      updated: c.updated,
      flagId: f.flagId,
      profileType: s.profileType,
      profileSubType: c.profileSubType,
      customerId: s.customer?.customerId,
      businessId: s.business?.businessId,
      customerName: s.customer?.name,
      businessName: s.business?.businessName,
      accountAvatar,
    };
  });
}

export { customerScenarios, businessScenarios, allProfileScenarios };
